/**
 * GetDeveloperPlanTenantsQuery - Fetches developer plan tenants with billing info
 * Follows MediatR pattern (Query + Handler in same file)
 */

import type { IQuery, IQueryHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import type { DeveloperPlanTenant, DeveloperPlanTenantsResponse } from './developer-plan.types';
import { toCamelCase } from '@/utils/caseTransform';

// ============================================================================
// Query (Request)
// ============================================================================

export class GetDeveloperPlanTenantsQuery implements IQuery<DeveloperPlanTenantsResponse> {
  readonly type = 'GetDeveloperPlanTenantsQuery' as const;
}

// ============================================================================
// Handler
// ============================================================================

export class GetDeveloperPlanTenantsQueryHandler
  implements IQueryHandler<GetDeveloperPlanTenantsQuery, DeveloperPlanTenantsResponse>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(_query: GetDeveloperPlanTenantsQuery): Promise<DeveloperPlanTenantsResponse> {
    try {
      const response = await this.httpClient.get<BaseApiResponse<any>>(
        '/api/v1/developer-plan'
      );

      if (!response.succeeded) {
        throw new Error(response.message || 'Failed to fetch developer plan tenants');
      }

      const rawData = response.data ?? [];

      // Transform each tenant item
      const tenants: DeveloperPlanTenant[] = (
        Array.isArray(rawData) ? rawData : []
      ).map((t: any) => toCamelCase(t));

      // Build unique status options for client-side filter
      const uniqueStatuses = [...new Set(tenants.map((t) => t.billingStatus).filter(Boolean))];
      const statusOptions = uniqueStatuses.map((s) => ({
        value: s,
        label: s,
      }));

      return { tenants, statusOptions };
    } catch (error: any) {
      if (error.response?.status === 403 || error.status === 403) {
        return { tenants: [], statusOptions: [] };
      }
      throw error;
    }
  }
}
